import { useQuery } from "@tanstack/react-query";
import { Plus, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { rag_v1 } from "@/utils/axios-v2";
import {
  IAutocompleteItem,
  ICollection,
  IMcpTool,
  ITool,
  Tools,
} from "@/types";
import { Button } from "@/components/ui/button";
import { MenuSelection } from "@/pages/Playground/components/agent-tools/menu-list";
import ToolIcon from "@/pages/Playground/components/agent-tools/agent-tool-icons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Typography } from "@mui/material";

interface SelectedResource {
  id: string;
  name: string;
  type:
    | "tool"
    | "collection"
    | "document"
    | "flow"
    | "mcpConnection"
    | "mcpTool";
  toolId?: string;
  icon?: string;
  connectionName?: string;
}

const DEFAULT_ICONS: Record<SelectedResource["type"], string> = {
  tool: "wrench",
  collection: "library-big",
  document: "file-text",
  flow: "workflow",
  mcpConnection: "mcp-default",
  mcpTool: "mcp-default",
};

const resourceDisplayName = (resource: SelectedResource): string =>
  resource.type === "mcpTool" && resource.connectionName
    ? `${resource.connectionName}: ${resource.name}`
    : resource.name;

export interface BlueprintAgentToolsPickerProps {
  selectedTools?: string[];
  selectedCollectionIds?: string[];
  selectedDocumentIds?: string[];
  selectedMcpConnectionIds?: string[];
  selectedMcpToolIds?: string[];
  onChange: (selection: {
    tools: string[];
    collectionIds: string[];
    documentIds: string[];
    mcpConnectionIds: string[];
    mcpToolIds: string[];
  }) => void;
}

const isMcpConnection = (tool: ITool | null): boolean => tool?.type === "mcp";

const createInitialResources = (
  selectedTools: string[],
  selectedCollectionIds: string[],
  selectedDocumentIds: string[],
  selectedMcpConnectionIds: string[],
  selectedMcpToolIds: string[],
  resourceNames: Record<string, string>,
  resourceIcons: Record<string, string>,
  mcpToolConnectionNames: Record<string, string>,
): SelectedResource[] => [
  ...selectedTools.map((id) => ({
    id,
    name: resourceNames[id] || id,
    type: "tool" as const,
    icon: resourceIcons[id] || DEFAULT_ICONS.tool,
  })),
  ...selectedCollectionIds.map((id) => ({
    id,
    name: resourceNames[id] || id,
    type: "collection" as const,
    icon: DEFAULT_ICONS.collection,
  })),
  ...selectedDocumentIds.map((id) => ({
    id,
    name: resourceNames[id] || id,
    type: "document" as const,
    icon: DEFAULT_ICONS.document,
  })),
  ...selectedMcpConnectionIds.map((id) => ({
    id,
    name: resourceNames[id] || id,
    type: "mcpConnection" as const,
    icon: resourceIcons[id] || DEFAULT_ICONS.mcpConnection,
  })),
  ...selectedMcpToolIds.map((id) => ({
    id,
    name: resourceNames[id] || id,
    type: "mcpTool" as const,
    icon: resourceIcons[id] || DEFAULT_ICONS.mcpTool,
    connectionName: mcpToolConnectionNames[id],
  })),
];

export default function BlueprintAgentToolsPicker({
  selectedTools = [],
  selectedCollectionIds = [],
  selectedDocumentIds = [],
  selectedMcpConnectionIds = [],
  selectedMcpToolIds = [],
  onChange,
}: BlueprintAgentToolsPickerProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [menuType, setMenuType] = useState<"tools" | "collections">("tools");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTool, setSelectedTool] = useState<ITool | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [resourceNames, setResourceNames] = useState<Record<string, string>>(
    {},
  );
  const [resourceIcons, setResourceIcons] = useState<Record<string, string>>(
    {},
  );
  const [mcpToolConnectionNames, setMcpToolConnectionNames] = useState<
    Record<string, string>
  >({});
  const { data: selectedResourceItems = [] } = useQuery<
    Array<IAutocompleteItem>
  >({
    queryKey: ["blueprint-selected-resources"],
    queryFn: () =>
      rag_v1
        .get("agent-tools/autocomplete/", { params: { search: "" } })
        .then((response) => response.data || []),
    enabled: Boolean(
      selectedCollectionIds.length || selectedDocumentIds.length,
    ),
  });

  useEffect(() => {
    if (!selectedResourceItems.length) return;
    setResourceNames((current) => ({
      ...current,
      ...Object.fromEntries(
        selectedResourceItems.map((item) => [item.id, item.name]),
      ),
    }));
  }, [selectedResourceItems]);

  const [resources, setResources] = useState<SelectedResource[]>(() =>
    createInitialResources(
      selectedTools,
      selectedCollectionIds,
      selectedDocumentIds,
      selectedMcpConnectionIds,
      selectedMcpToolIds,
      resourceNames,
      resourceIcons,
      mcpToolConnectionNames,
    ),
  );

  useEffect(() => {
    setResources(
      createInitialResources(
        selectedTools,
        selectedCollectionIds,
        selectedDocumentIds,
        selectedMcpConnectionIds,
        selectedMcpToolIds,
        resourceNames,
        resourceIcons,
        mcpToolConnectionNames,
      ),
    );
  }, [
    selectedTools,
    selectedCollectionIds,
    selectedDocumentIds,
    selectedMcpConnectionIds,
    selectedMcpToolIds,
    resourceNames,
    resourceIcons,
    mcpToolConnectionNames,
  ]);

  const { data: tools = [], isLoading: toolsLoading } = useQuery<Tools>({
    queryKey: ["tools"],
    queryFn: () =>
      rag_v1.get("agent-tools/tools/").then((response) => response.data),
  });

  useEffect(() => {
    if (!tools.length) return;

    setResourceNames((current) => ({
      ...current,
      ...Object.fromEntries(
        tools.flatMap((tool) => [
          [tool.id, tool.name],
          ...(tool.source_metadata?.mcp_connection_id
            ? [[tool.source_metadata.mcp_connection_id, tool.name]]
            : []),
          ...(tool.tools || []).map((mcpTool) => [mcpTool.id, mcpTool.name]),
        ]),
      ),
    }));

    setResourceIcons((current) => ({
      ...current,
      ...Object.fromEntries(
        tools.flatMap((tool) => {
          const icon =
            tool.icon_url ||
            tool.icon ||
            (tool.type === "mcp"
              ? DEFAULT_ICONS.mcpConnection
              : DEFAULT_ICONS.tool);
          return [
            [tool.id, icon],
            ...(tool.source_metadata?.mcp_connection_id
              ? [[tool.source_metadata.mcp_connection_id, icon]]
              : []),
            ...(tool.tools || []).map((mcpTool) => [
              mcpTool.id,
              mcpTool.icon || DEFAULT_ICONS.mcpTool,
            ]),
          ];
        }),
      ),
    }));

    setMcpToolConnectionNames((current) => ({
      ...current,
      ...Object.fromEntries(
        tools.flatMap((tool) =>
          (tool.tools || []).map((mcpTool) => [mcpTool.id, tool.name]),
        ),
      ),
    }));
  }, [tools]);

  const { data: autocompleteItems = [], isLoading: autocompleteLoading } =
    useQuery<Array<IAutocompleteItem>>({
      queryKey: ["blueprint-autocomplete", searchQuery],
      queryFn: () =>
        rag_v1
          .get("agent-tools/autocomplete/", { params: { search: searchQuery } })
          .then((response) => response.data || []),
      enabled: isOpen && menuType === "collections" && !selectedTool,
    });

  const { data: collectionItems = [], isLoading: collectionLoading } = useQuery<
    Array<ICollection>
  >({
    queryKey: ["blueprint-autocomplete", selectedTool?.id, searchQuery],
    queryFn: () =>
      rag_v1
        .get(`agent-tools/autocomplete/${selectedTool?.id}`, {
          params: { search: searchQuery },
        })
        .then((response) => response.data || []),
    enabled:
      isOpen &&
      menuType === "collections" &&
      Boolean(selectedTool) &&
      !isMcpConnection(selectedTool),
  });

  const mcpItems = useMemo(() => {
    if (!isMcpConnection(selectedTool)) return [];
    const query = searchQuery.trim().toLowerCase();
    return (selectedTool?.tools || []).filter(
      (item) =>
        !query ||
        item.name.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query),
    );
  }, [searchQuery, selectedTool]);

  const visibleCollections = selectedTool
    ? selectedTool.type === "mcp"
      ? mcpItems
      : collectionItems
    : autocompleteItems;
  const visibleTools = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return query
      ? tools.filter((tool) => tool.name.toLowerCase().includes(query))
      : tools;
  }, [searchQuery, tools]);
  const isLoading = selectedTool
    ? selectedTool.type === "mcp"
      ? false
      : collectionLoading
    : autocompleteLoading;

  useEffect(() => {
    setSelectedIndex(0);
  }, [menuType, searchQuery, selectedTool, visibleCollections.length]);

  // mcpConnection is tracked internally for mcpConnectionIds but no longer shown as its own chip.
  const visibleResources = useMemo(
    () => resources.filter((resource) => resource.type !== "mcpConnection"),
    [resources],
  );

  const emitChange = (nextResources: SelectedResource[]) => {
    setResources(nextResources);
    onChange({
      tools: nextResources
        .filter((item) => item.type === "tool")
        .map((item) => item.id),
      collectionIds: nextResources
        .filter((item) => item.type === "collection" || item.type === "flow")
        .map((item) => item.id),
      documentIds: nextResources
        .filter((item) => item.type === "document")
        .map((item) => item.id),
      mcpConnectionIds: nextResources
        .filter((item) => item.type === "mcpConnection")
        .map((item) => item.id),
      mcpToolIds: nextResources
        .filter((item) => item.type === "mcpTool")
        .map((item) => item.id),
    });
  };

  const addResource = (resource: SelectedResource) => {
    if (
      resources.some(
        (item) => item.id === resource.id && item.type === resource.type,
      )
    )
      return;
    setResourceNames((current) => ({
      ...current,
      [resource.id]: resource.name,
    }));
    emitChange([...resources, resource]);
  };

  const addResources = (newResources: SelectedResource[]) => {
    const nextResources = newResources.reduce((current, resource) => {
      if (
        current.some(
          (item) => item.id === resource.id && item.type === resource.type,
        )
      ) {
        return current;
      }
      setResourceNames((names) => ({ ...names, [resource.id]: resource.name }));
      return [...current, resource];
    }, resources);
    emitChange(nextResources);
  };

  const handleToolSelect = (tool: ITool) => {
    if (tool.has_autocomplete || tool.type === "mcp") {
      setSelectedTool(tool);
      setMenuType("collections");
      setSearchQuery("");
      setSelectedIndex(0);
      return;
    }

    addResource({
      id: tool.id,
      name: tool.name,
      type: "tool",
      icon: tool.icon_url || tool.icon || DEFAULT_ICONS.tool,
    });
    setIsOpen(false);
  };

  const handleCollectionSelect = (
    item: ICollection | IAutocompleteItem | IMcpTool,
  ) => {
    const mcpConnection = selectedTool;
    if (
      mcpConnection &&
      isMcpConnection(mcpConnection) &&
      "source_metadata" in item
    ) {
      const connectionId =
        mcpConnection.source_metadata?.mcp_connection_id || mcpConnection.id;
      const connectionIcon =
        mcpConnection.icon_url ||
        mcpConnection.icon ||
        DEFAULT_ICONS.mcpConnection;
      const resourcesToAdd: SelectedResource[] = [
        {
          id: item.id,
          name: item.name,
          type: "mcpTool",
          toolId: mcpConnection.id,
          icon: ("icon" in item && item.icon) || DEFAULT_ICONS.mcpTool,
          connectionName: mcpConnection.name,
        },
      ];

      if (connectionId) {
        resourcesToAdd.unshift({
          id: connectionId,
          name: mcpConnection.name,
          type: "mcpConnection",
          icon: connectionIcon,
        });
      }

      addResources(resourcesToAdd);
      setIsOpen(false);
      setMenuType("tools");
      setSelectedTool(null);
      setSearchQuery("");
      return;
    }

    const type =
      "type" in item && (item.type === "document" || item.type === "flow")
        ? item.type
        : "collection";
    addResource({
      id: item.id,
      name: item.name,
      type,
      toolId: selectedTool?.id,
      icon: DEFAULT_ICONS[type],
    });
    setIsOpen(false);
    setMenuType("tools");
    setSelectedTool(null);
    setSearchQuery("");
  };

  const openPicker = () => {
    setMenuType("tools");
    setSelectedTool(null);
    setSearchQuery("");
  };

  const handlePickerOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setMenuType("tools");
      setSelectedTool(null);
      setSearchQuery("");
    }
  };

  const removeResource = (resource: SelectedResource) => {
    emitChange(
      resources.filter(
        (item) => !(item.id === resource.id && item.type === resource.type),
      ),
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-center gap-2">
          <div>
            <Typography
              variant="body2"
              className="text-foreground text-sm font-semibold"
            >
              {t("blueprints.Tools")}
            </Typography>
            <Typography
              variant="body2"
              className="text-sm text-muted-foreground"
            >
              {t("blueprints.select_tools")}
            </Typography>
          </div>
          <div className="relative">
            <DropdownMenu open={isOpen} onOpenChange={handlePickerOpenChange}>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  onClick={openPicker}
                  aria-label={t("playground.availableTools")}
                  className={`transition-all p-2 cursor-pointer duration-150 `}
                >
                  <Plus className="text-foreground size-6 bg-secondary rounded-[4px] p-1" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                side="bottom"
                sideOffset={8}
                collisionPadding={8}
                className="w-[min(calc(100vw-2rem),240px)] border-0 bg-transparent p-0 shadow-none"
                onCloseAutoFocus={(event) => event.preventDefault()}
              >
                <MenuSelection
                  isOpen={isOpen}
                  tools={visibleTools}
                  position={{ left: 0 }}
                  onOpenChange={handlePickerOpenChange}
                  onSelect={handleToolSelect}
                  onCollectionSelect={handleCollectionSelect}
                  menuType={menuType}
                  collections={visibleCollections}
                  isLoading={toolsLoading || isLoading}
                  selectedIndex={selectedIndex}
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  selectedTool={selectedTool || undefined}
                  inline
                />
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <div className="flex min-h-10 w-full flex-wrap gap-2 rounded-[8px] border border-border bg-input p-2">
          {visibleResources.length ? (
            visibleResources.map((resource) => (
              <span
                key={`${resource.type}-${resource.id}`}
                className="flex items-center gap-1 rounded-full bg-secondary px-2 py-1 text-xs text-foreground"
              >
                <ToolIcon
                  name={resource.icon || DEFAULT_ICONS[resource.type]}
                  className="size-3.5"
                />
                {resourceDisplayName(resource)}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-4 rounded-full p-0"
                  onClick={() => removeResource(resource)}
                  aria-label={`Remove ${resource.name}`}
                >
                  <X className="size-3" />
                </Button>
              </span>
            ))
          ) : (
            <span className="text-sm text-muted-foreground">
              {t("blueprints.noToolsSelected")}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
